import { useEffect, useState } from "react";
import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { StyledModalTitle } from "../../../@ui/StyledModalTitle";
import styled from "styled-components";
import { QueueItem } from "../../../types/Queue/QueueItem";
import { QueueStatus } from "../../../types/Queue/QueueStatus";
import WfsParametersDisplay from "../../modal/OrderItem/WfsParametersDisplay";
import useQueueService from "../../../hooks/useQueueService";
import ThemedModal from "../../../@ui/Modal/ThemedModal";

interface OrderItemModalProps {
  queueItemId: string;
  onClose: () => void;
  retry: (orderItemId: string) => void;
  markAsResolved: (orderItemId: string) => void;
}

const Section = styled.div`
  padding-top: 0.5rem;
  border-top: 1px solid #ccc;
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
`;

const QueueItemDetails = ({
  queueItemId,
  onClose,
  retry,
  markAsResolved,
}: OrderItemModalProps) => {
  const queueService = useQueueService();
  const [queueItem, setQueueItem] = useState<QueueItem | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!queueItemId) return;
    const abortController = new AbortController();
    setLoading(true);
    queueService
      .GetQueueItem(queueItemId, abortController)
      .then((queueItem) => {
        setQueueItem(queueItem);
        setLoading(false);
      })
      .catch(() => {});

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueItemId]);

  return (
    <ThemedModal onHide={onClose} centered scrollable show={true}>
      <Modal.Header className="bg-secondary text-white" closeButton>
        <StyledModalTitle>Deferred Request Details</StyledModalTitle>
      </Modal.Header>
      <Modal.Body className="overflow-auto">
        {loading && (
          <div className="d-flex justify-content-center">
            <Spinner />
          </div>
        )}
        {!loading && queueItem && (
          <div className="d-flex flex-column gap-1">
            <Row>
              <Col md="3">Deferred Request Id</Col>
              <Col md="9">{queueItem.deferredRequestId}</Col>
            </Row>
            <Row>
              <Col md="3">Order Item Id</Col>
              <Col md="9">{queueItem.orderItemId}</Col>
            </Row>
            <Row>
              <Col md="3">User</Col>
              <Col md="9">{queueItem.userFullName}</Col>
            </Row>
            <Row>
              <Col md="3">Organisation</Col>
              <Col md="9">{queueItem.organisationName}</Col>
            </Row>
            <Row>
              <Col md="3">Status</Col>
              <Col md="9">
                {queueItem.queueStatus === QueueStatus.QUEUED && <>Queued</>}
                {queueItem.queueStatus === QueueStatus.PROCESSING && (
                  <>Processing</>
                )}
                {queueItem.queueStatus === QueueStatus.COMPLETED && (
                  <>Completed</>
                )}
                {queueItem.queueStatus === QueueStatus.FAILED && <>Failed</>}
              </Col>
            </Row>
            {queueItem.queueStatus === QueueStatus.FAILED && (
              <Row>
                <Col md="3">Failure Reason</Col>
                <Col md="9">{queueItem.failureReason}</Col>
              </Row>
            )}
            <Row>
              <Col md="3">Queued</Col>
              <Col md="9">
                {queueItem.queuedDate.toFormat("dd/MM/yyyy HH:mm")}
                <br />({queueItem.timeOnQueue.toFixed(0)} mins)
              </Col>
            </Row>
            <Row>
              <Col md="3">Started Processing</Col>
              <Col md="9">
                {queueItem.startedProcessingDate && (
                  <>
                    {queueItem.startedProcessingDate.toFormat(
                      "dd/MM/yyyy HH:mm",
                    )}
                    <br />({queueItem.processingTime!.toFixed(0)} mins /
                    {(queueItem.queueStatus === QueueStatus.PROCESSING ||
                      queueItem.queueStatus === QueueStatus.FAILED) && (
                      <> {(queueItem.progress * 100).toFixed(0)}%</>
                    )}
                    {queueItem.queueStatus === QueueStatus.COMPLETED && (
                      <> 100%</>
                    )}
                    )
                  </>
                )}
              </Col>
            </Row>
            <Row>
              <Col md="3">Completed</Col>
              <Col md="9">
                {queueItem.completedDate && (
                  <>{queueItem.completedDate.toFormat("dd/MM/yyyy HH:mm")}</>
                )}
              </Col>
            </Row>
            <Row>
              <Col md="3">Retry Attempt</Col>
              <Col md="9">{queueItem.retryAttempt}</Col>
            </Row>

            <Section>
              <Row>
                <Col md="3">Product</Col>
                <Col md="9">{queueItem.layerName}</Col>
              </Row>
              <WfsParametersDisplay
                areaDescription={queueItem.areaDescription}
                wfsParameters={queueItem.wfsParameters}
              />
            </Section>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" onClick={onClose}>
          Close
        </Button>
        {queueItem && queueItem.queueStatus === QueueStatus.FAILED && (
          <>
            <Button
              variant="warning"
              onClick={() => {
                onClose();
                retry(queueItem.orderItemId);
              }}>
              Retry
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                onClose();
                markAsResolved(queueItem.orderItemId);
              }}>
              Mark as Resolved
            </Button>
          </>
        )}
      </Modal.Footer>
    </ThemedModal>
  );
};

export default QueueItemDetails;
